import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { loginWithEmail, isAuthenticated } from '../mockData';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, KeyRound, X, Mail, LockKeyhole, Eye, EyeOff, ShieldCheck, Droplets, Waves, FlaskConical, Leaf, ChartColumn, ArrowRight, TrendingUp, Users, FileText, Headphones } from 'lucide-react';
import '../styles/login-scene.css';

const capabilities = [
  { title: 'Groundwater', detail: 'Level • Quality', Icon: Droplets, kind: 'groundwater' },
  { title: 'Flow Monitoring', detail: 'Totaliser • Flow Rate', Icon: Waves, kind: 'flow' },
  { title: 'Water Quality', detail: 'STP • DO • OCEMS', Icon: FlaskConical, kind: 'quality' },
  { title: 'Environment', detail: 'Soil • Air • Biodiversity', Icon: Leaf, kind: 'environment' },
  { title: 'Data to Action', detail: 'Reports • Alerts • Insights', Icon: ChartColumn, kind: 'data' },
];

const MonitoringCapabilities = () => {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [focused, setFocused] = useState(null);
  const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    if (reduced || hovered !== null || focused !== null) return;
    const timer = window.setInterval(() => setActive(value => (value + 1) % capabilities.length), 2200);
    return () => window.clearInterval(timer);
  }, [reduced, hovered, focused, active]);
  const selected = hovered ?? focused ?? active;
  return <aside className="env-capabilities" aria-label="Monitoring capabilities">
    <p className="env-eyebrow">CONNECTED INTELLIGENCE</p>
    <h2>Every measurement.<br />A clearer perspective.</h2>
    <div className="env-capability-list">
      {capabilities.map(({ title, detail, Icon, kind }, index) => <button
        key={title} type="button" className={`env-capability ${kind} ${selected === index ? 'is-active' : ''}`}
        aria-pressed={selected === index} onMouseEnter={() => setHovered(index)} onMouseLeave={() => setHovered(null)}
        onFocus={() => setFocused(index)} onBlur={() => setFocused(null)} onClick={() => setActive(index)}>
        <span className="env-capability-icon"><Icon size={23} aria-hidden="true" /></span>
        <span className="env-capability-copy"><strong>{title}</strong><span>{detail}</span></span>
        <span className="env-capability-dot" aria-hidden="true" />
      </button>)}
    </div>
    <p className="env-capability-note">Monitoring that turns data into decisions.</p>
  </aside>;
};

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Admin OTP-based password recovery
  const [showRecover, setShowRecover] = useState(false);
  const [recoverStep, setRecoverStep] = useState(1); // 1=request OTP, 2=verify + set new
  const [otp, setOtp] = useState('');
  const [newPass, setNewPass] = useState('');
  const [recoverErr, setRecoverErr] = useState('');
  const [recoverMsg, setRecoverMsg] = useState('');
  const [recoverBusy, setRecoverBusy] = useState(false);

  const openRecover = () => {
    setShowRecover(true);
    setRecoverStep(1);
    setOtp('');
    setNewPass('');
    setRecoverErr('');
    setRecoverMsg('');
  };

  const requestOtp = async () => {
    setRecoverErr('');
    setRecoverMsg('');
    setRecoverBusy(true);
    try {
      await api.post('/api/auth/admin-recovery/request-otp');
      setRecoverMsg('OTP sent to the admin recovery mailbox. Check inbox and enter the 6-digit code below.');
      setRecoverStep(2);
    } catch (e) {
      setRecoverErr(e?.response?.data?.detail || 'Failed to send OTP. Try again in a minute.');
    } finally {
      setRecoverBusy(false);
    }
  };

  const submitOtp = async () => {
    setRecoverErr('');
    setRecoverMsg('');
    if (!/^\d{6}$/.test(otp)) { setRecoverErr('Enter the 6-digit OTP.'); return; }
    if (newPass.length < 8) { setRecoverErr('New password must be at least 8 characters.'); return; }
    setRecoverBusy(true);
    try {
      await api.post('/api/auth/admin-recovery/verify-otp', { otp, new_password: newPass });
      setRecoverMsg('Admin password updated. You can now sign in with the new password.');
      setTimeout(() => setShowRecover(false), 2000);
    } catch (e) {
      setRecoverErr(e?.response?.data?.detail || 'Invalid or expired OTP.');
    } finally {
      setRecoverBusy(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
    // `isAuthenticated` is a stable import (module-level function), safe to omit.
    // `navigate` is stable per react-router. Effect only needs to run once on mount.
  }, [navigate]);

  // Client self-serve password recovery (email OTP)
  const [showClientRecover, setShowClientRecover] = useState(false);
  const [clientRecoverStep, setClientRecoverStep] = useState(1);
  const [clientIdent, setClientIdent] = useState('');
  const [clientOtp, setClientOtp] = useState('');
  const [clientNewPass, setClientNewPass] = useState('');
  const [clientRecoverBusy, setClientRecoverBusy] = useState(false);
  const [clientRecoverErr, setClientRecoverErr] = useState('');
  const [clientRecoverMsg, setClientRecoverMsg] = useState('');

  const openClientRecover = () => {
    setShowClientRecover(true);
    setClientRecoverStep(1);
    setClientIdent(email || '');
    setClientOtp('');
    setClientNewPass('');
    setClientRecoverErr('');
    setClientRecoverMsg('');
  };

  const requestClientOtp = async () => {
    setClientRecoverErr('');
    setClientRecoverMsg('');
    if (!clientIdent.trim()) { setClientRecoverErr('Enter your username or email.'); return; }
    setClientRecoverBusy(true);
    try {
      await api.post('/api/auth/password-recovery/request-otp', { identifier: clientIdent.trim() });
      setClientRecoverMsg('If the account exists, an OTP has been sent to its registered email. Check the inbox.');
      setClientRecoverStep(2);
    } catch (e) {
      setClientRecoverErr(e?.response?.data?.detail || 'Could not send OTP. Try again shortly.');
    } finally {
      setClientRecoverBusy(false);
    }
  };

  const submitClientOtp = async () => {
    setClientRecoverErr('');
    setClientRecoverMsg('');
    if (!/^\d{6}$/.test(clientOtp)) { setClientRecoverErr('Enter the 6-digit OTP.'); return; }
    if (clientNewPass.length < 8) { setClientRecoverErr('New password must be at least 8 characters.'); return; }
    setClientRecoverBusy(true);
    try {
      await api.post('/api/auth/password-recovery/verify-otp', {
        identifier: clientIdent.trim(),
        otp: clientOtp,
        new_password: clientNewPass,
      });
      setClientRecoverMsg('Password updated. You can sign in now.');
      setTimeout(() => setShowClientRecover(false), 2000);
    } catch (e) {
      setClientRecoverErr(e?.response?.data?.detail || 'Invalid or expired OTP.');
    } finally {
      setClientRecoverBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    const result = await loginWithEmail(email.trim(), password);
    setSubmitting(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }
  };

  return (
    <div className="env-scene env-scene-photo" data-testid="login-scene">
      <div className="env-landscape" style={{ backgroundImage: `url("${process.env.PUBLIC_URL}/images/login-landscape-v3.webp")` }} aria-hidden="true">
        <div className="env-cloud cloud-one" /><div className="env-cloud cloud-two" />
        <div className="env-photo-water" />
        {Array.from({ length: 7 }, (_, index) => <div key={index} className={`env-fish-jump fish-${index}`}>
          <svg className="env-jumping-fish" viewBox="0 0 40 20" focusable="false">
            <path d="M9 10 L1 3 L1 17 Z" fill="#89bbc5" />
            <path d="M8 10 Q22 -3 37 10 Q22 23 8 10 Z" fill="#b9d7d7" />
            <path d="M16 5 L21 1 L25 5" fill="#7da8b6" />
            <circle cx="31" cy="8" r="1.3" fill="#173948" />
          </svg>
          <span className="env-fish-splash" />
        </div>)}
        <svg className="env-windfarm" viewBox="0 0 1672 941" preserveAspectRatio="xMaxYMid slice">
          {[[560, 610, 68], [665, 605, 58], [1110, 600, 60], [1240, 605, 72], [1360, 620, 58]].map(([x, base, height], index) => <g key={x}>
            <path d={`M${x} ${base} V${base - height}`} className="env-farm-tower" />
            <g transform={`translate(${x} ${base - height})`}>
              <g className="env-farm-rotor" style={{ animationDuration: `${22 + index * 2}s` }}>
                {[0, 120, 240].map(angle => <path key={angle} d="M-1 0 L-2 -24 Q0 -28 2 -24 L1 0 Z" transform={`rotate(${angle})`} />)}
              </g>
              <circle r="2.5" fill="#dce9eb" />
            </g>
          </g>)}
        </svg>
        {Array.from({ length: 7 }, (_, index) => <div key={index} className="env-bird env-flock-bird" style={{
          top: `${7 + (index % 4) * 6}%`,
          animationDuration: `${38 + index * 3}s`,
          animationDelay: `-${5 + index * 7}s`,
        }} />)}
        {Array.from({ length: 7 }, (_, index) => <div key={index} className={`env-floating-leaf env-wind-leaf wind-leaf-${index}`} style={{
          top: `${25 + (index % 4) * 12}%`, animationDuration: `${17 + index * 2}s`, animationDelay: `${-3 - index * 4}s`,
        }}><span className="env-leaf-orbit"><Leaf size={34} aria-hidden="true" /></span></div>)}
      </div>
      <div className="env-page-content">
        <header className="env-page-header"><span>ENVIRONMENTAL MONITORING PORTAL</span><span><ShieldCheck size={14} /> Secure access</span></header>
        <main className="env-login-layout">
          <section className="env-intro" aria-labelledby="env-intro-title">
            <p className="env-eyebrow">MEASURE. UNDERSTAND. PROTECT.</p>
            <h1 id="env-intro-title">Environmental <span>Intelligence</span><br />for a <em>Sustainable Tomorrow</em></h1>
            <div className="env-intro-line" />
            <p>Real-time monitoring.<br />Better insights.<br />Cleaner and healthier environment.</p>
            <span className="env-intro-caption">A better future starts with better information.</span>
          </section>
          <section className="env-login-card" data-testid="login-card" aria-labelledby="login-title">
            <div className="env-official-brand">
              <img className="env-official-logo" src={`${process.env.PUBLIC_URL}/images/My NEW LOGO(4).png`} alt="Envirolytics Sustainability emblem" />
              <img className="env-official-wordmark" src={`${process.env.PUBLIC_URL}/images/Envirolytics Company Name(4).png?v=20260930-transparent`} alt="Envirolytics Sustainability Private Limited" />
            </div>
            <h2 id="login-title">Welcome Back</h2>
            <p className="env-login-description">Sign in to your environmental monitoring portal</p>
            {error && <div className="env-login-error" role="alert" data-testid="login-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <Label htmlFor="email" className="env-field-label">USERNAME OR EMAIL</Label>
              <div className="env-input-wrap"><Mail size={18} aria-hidden="true" />
                <Input id="email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your username or email" data-testid="login-email-input" autoComplete="username" />
              </div>
              <Label htmlFor="password" className="env-field-label">PASSWORD</Label>
              <div className="env-input-wrap"><LockKeyhole size={18} aria-hidden="true" />
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" data-testid="login-password-input" autoComplete="current-password" />
                <button type="button" className="env-password-toggle" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} aria-controls="password">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
              <div className="env-recovery-row"><button type="button" onClick={openClientRecover} data-testid="forgot-password-link">Forgot password?</button></div>
              <Button type="submit" disabled={submitting} className="env-submit" data-testid="login-submit-button">
                {submitting ? <><Loader2 size={18} className="animate-spin" />Signing in…</> : <>Sign Me In<ArrowRight size={18} /></>}
              </Button>
            </form>
            <p className="env-secure"><ShieldCheck size={15} aria-hidden="true" /> Secure access</p>
            <div className="env-card-links"><Link to="/policies">Policies</Link><span>·</span><button type="button" onClick={openRecover} data-testid="forgot-admin-password-link">Admin recovery</button></div>
          </section>
          <MonitoringCapabilities />
        </main>
        <section className="env-information" aria-label="Platform benefits">
          <svg className="env-benefit-wave" viewBox="0 0 1800 70" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <path d="M0 55 Q230 -10 460 27 T900 30 T1360 30 T1800 55 V70 H0Z" fill="#0c83b7" fillOpacity=".48" />
            <path d="M0 62 Q220 8 440 38 T900 40 T1360 37 T1800 62 V70 H0Z" fill="#0877a7" fillOpacity=".65" stroke="#20b9ec" strokeWidth="1.4" />
            <path d="M0 66 Q235 22 470 44 T900 47 T1370 44 T1800 66 V70 H0Z" fill="#03283b" fillOpacity=".92" />
          </svg>
          {[
            [TrendingUp, 'Real-time Data', 'Continuous Monitoring'],
            [ShieldCheck, 'Compliance Support', 'For a Cleaner Environment'],
            [Leaf, 'Sustainable Solutions', 'People • Planet • Progress'],
            [Users, 'Trusted by Industry', 'Across Sectors'],
          ].map(([Icon, title, detail]) => <div key={title}><Icon size={21} aria-hidden="true" /><span><strong>{title}</strong><small>{detail}</small></span></div>)}
        </section>
        <footer className="env-footer"><nav aria-label="Legal and support"><Link to="/policies"><FileText size={22} aria-hidden="true" />Privacy Policy</Link><Link to="/policies"><ShieldCheck size={23} aria-hidden="true" />Terms of Use</Link><span><Headphones size={23} aria-hidden="true" />Support</span></nav><p>Version 1.0 <span>·</span> Envirolytics Sustainability Pvt. Ltd.</p></footer>
      </div>

      {/* Client self-serve password recovery modal (OTP to registered email) */}
      {showClientRecover && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          data-testid="client-recovery-modal"
        >
          <div className="w-full max-w-md mx-4 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-6 text-white relative">
            <button
              type="button"
              onClick={() => setShowClientRecover(false)}
              className="absolute top-3 right-3 text-white/60 hover:text-white bg-transparent border-0 cursor-pointer"
              data-testid="client-recovery-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <KeyRound size={18} className="text-sky-400" />
              <h3 className="text-lg font-semibold">Reset Your Password</h3>
            </div>
            <p className="text-white/70 text-xs mb-4">
              Enter your username or the email on your Envirolytics account. A
              6-digit code will be sent to your registered email.
            </p>

            {clientRecoverStep === 1 && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/80 mb-1 block">Username or Email</Label>
                  <Input
                    type="text"
                    value={clientIdent}
                    onChange={(e) => setClientIdent(e.target.value)}
                    placeholder="e.g. lakecity-stp or rajiv.kumar@shalimar.org"
                    className="text-white"
                    data-testid="client-recovery-ident-input"
                    autoFocus
                  />
                </div>
                <Button
                  type="button"
                  onClick={requestClientOtp}
                  disabled={clientRecoverBusy}
                  className="w-full bg-sky-500 hover:bg-sky-400 text-white"
                  data-testid="client-recovery-request-otp"
                >
                  {clientRecoverBusy ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Sending…</span> : 'Send OTP to my email'}
                </Button>
              </div>
            )}

            {clientRecoverStep === 2 && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/80 mb-1 block">6-digit OTP</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={clientOtp}
                    onChange={(e) => setClientOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="text-center text-lg tracking-[0.5em] font-mono text-white"
                    data-testid="client-recovery-otp-input"
                    autoFocus
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/80 mb-1 block">New password</Label>
                  <Input
                    type="password"
                    value={clientNewPass}
                    onChange={(e) => setClientNewPass(e.target.value)}
                    placeholder="Min 8 characters"
                    className="text-white"
                    data-testid="client-recovery-newpass-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={submitClientOtp}
                  disabled={clientRecoverBusy}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-white"
                  data-testid="client-recovery-verify-otp"
                >
                  {clientRecoverBusy ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Verifying…</span> : 'Reset password'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setClientRecoverStep(1); setClientOtp(''); setClientNewPass(''); setClientRecoverErr(''); setClientRecoverMsg(''); }}
                  className="text-xs text-white/60 hover:text-white underline bg-transparent border-0 cursor-pointer w-full text-center"
                  data-testid="client-recovery-resend"
                >
                  Didn&apos;t get it? Send a new OTP
                </button>
              </div>
            )}

            {clientRecoverMsg && (
              <div
                className="mt-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2"
                data-testid="client-recovery-message"
              >
                {clientRecoverMsg}
              </div>
            )}
            {clientRecoverErr && (
              <div
                className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
                data-testid="client-recovery-error"
              >
                {clientRecoverErr}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Admin recovery modal (OTP to pre-configured recovery mailbox) */}
      {showRecover && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          data-testid="admin-recovery-modal"
        >
          <div className="w-full max-w-md mx-4 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-6 text-white relative">
            <button
              type="button"
              onClick={() => setShowRecover(false)}
              className="absolute top-3 right-3 text-white/60 hover:text-white bg-transparent border-0 cursor-pointer"
              data-testid="admin-recovery-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <KeyRound size={18} className="text-sky-400" />
              <h3 className="text-lg font-semibold">Admin Password Recovery</h3>
            </div>
            <p className="text-white/70 text-xs mb-4">
              A 6-digit OTP will be emailed to the pre-registered admin recovery mailbox.
              Only the mailbox owner can complete the reset.
            </p>

            {recoverStep === 1 && (
              <div className="space-y-3">
                <Button
                  type="button"
                  onClick={requestOtp}
                  disabled={recoverBusy}
                  className="w-full bg-sky-500 hover:bg-sky-400 text-white"
                  data-testid="admin-recovery-request-otp"
                >
                  {recoverBusy ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Sending…</span> : 'Send OTP to admin mailbox'}
                </Button>
              </div>
            )}

            {recoverStep === 2 && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/80 mb-1 block">6-digit OTP</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="text-center text-lg tracking-[0.5em] font-mono text-white"
                    data-testid="admin-recovery-otp-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/80 mb-1 block">New admin password</Label>
                  <Input
                    type="password"
                    value={newPass}
                    onChange={(e) => setNewPass(e.target.value)}
                    placeholder="Min 8 characters"
                    className="text-white"
                    data-testid="admin-recovery-newpass-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={submitOtp}
                  disabled={recoverBusy}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-white"
                  data-testid="admin-recovery-verify-otp"
                >
                  {recoverBusy ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Verifying…</span> : 'Reset admin password'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setRecoverStep(1); setOtp(''); setNewPass(''); setRecoverErr(''); setRecoverMsg(''); }}
                  className="text-xs text-white/60 hover:text-white underline bg-transparent border-0 cursor-pointer w-full text-center"
                  data-testid="admin-recovery-resend"
                >
                  Didn&apos;t get it? Send a new OTP
                </button>
              </div>
            )}

            {recoverMsg && (
              <div
                className="mt-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2"
                data-testid="admin-recovery-message"
              >
                {recoverMsg}
              </div>
            )}
            {recoverErr && (
              <div
                className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
                data-testid="admin-recovery-error"
              >
                {recoverErr}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Login;
